import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Barcode, FileSpreadsheet, ShieldCheck, Mail, Lock, ArrowRight, CheckCircle2, Hash, KeyRound } from 'lucide-react';
import { login, loginPin } from '../api.js';
import { Alert, Button, Field, Input } from '../ui/index.js';

type LoginMode = 'password' | 'pin';

const PIPELINE = [
  {
    step: '01',
    title: 'GTIN & Barcodes',
    text: 'Chaque code-barres est décodé, vérifié par checksum et corroboré par les sources partenaires.',
    icon: Barcode,
  },
  {
    step: '02',
    title: "Prix d'achat & TVA",
    text: 'Les listes grossistes sont réconciliées automatiquement pour calculer le prix net HT exact.',
    icon: FileSpreadsheet,
  },
  {
    step: '03',
    title: 'Export Sage 100c',
    text: 'Le fichier prêt pour intégration comptable est généré sans retouche manuelle.',
    icon: ShieldCheck,
  },
] as const;

/** Modern SaaS Login Screen: split layout, sleek card, high-contrast branding. */
export function LoginPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState<LoginMode>('password');
  const loginMutation = useMutation({
    mutationFn: () => (mode === 'pin' ? loginPin(email, pin) : login(email, password)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <div className="mx-auto grid min-h-screen w-full max-w-7xl lg:grid-cols-[1.1fr_0.9fr]">
        {/* Left Side: Brand Narrative & Pipeline Highlights */}
        <section className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:py-20 lg:pr-16">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-herb-600 to-herb-800 text-white shadow-xs"
            >
              <span className="font-bold text-lg leading-none">M</span>
            </span>
            <div>
              <p className="font-bold text-xl leading-none text-ink tracking-tight">MaPara</p>
              <p className="eyebrow mt-1 text-herb-700">GTIN · Prix · Sage 100c</p>
            </div>
          </div>

          <h1 className="mt-8 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[42px] lg:leading-[1.15]">
            Réconciliation catalogue,
            <br />
            <span className="text-herb-600">précision ligne par ligne.</span>
          </h1>
          <p className="mt-4 max-w-lg text-sm sm:text-base leading-relaxed text-ink-soft">
            Plateforme de contrôle et d&apos;enrichissement GTIN : validation algorithmique,
            comparateur de sources parapharmacie et génération d&apos;exports certifiés Sage 100c.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {PIPELINE.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.step}
                  className="rounded-xl border border-border bg-card p-4 shadow-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-herb-600">{item.step}</span>
                    <Icon className="h-4 w-4 text-ink-ghost" />
                  </div>
                  <p className="mt-2.5 text-sm font-semibold text-ink">{item.title}</p>
                  <p className="mt-1 text-xs text-ink-soft leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex items-center gap-6 text-xs text-ink-faint">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-herb-600" />
              Validation humaine obligatoire
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-herb-600" />
              Journalisation d&apos;audit
            </span>
          </div>
        </section>

        {/* Right Side: Sleek Login Card */}
        <section className="flex items-center justify-center border-t border-border bg-card/60 p-6 sm:p-12 lg:border-l lg:border-t-0">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-pop">
            <div className="flex items-center justify-between">
              <span className="eyebrow text-herb-700">Accès Opérateur</span>
              <span className="h-2 w-2 rounded-full bg-herb-600 animate-pulse-soft" />
            </div>

            <h2 className="mt-3 text-xl font-bold tracking-tight text-ink">Connexion à la console</h2>
            <p className="mt-1 text-xs text-ink-soft">
              Chaque action effectuée est horodatée et attribuée à votre compte.
            </p>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMode('password')}
                className={
                  mode === 'password'
                    ? 'flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-herb-600/30 bg-herb-50 px-3 py-1.5 text-xs font-semibold text-herb-800'
                    : 'flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-cream'
                }
              >
                <Lock className="h-3.5 w-3.5" /> Mot de passe
              </button>
              <button
                type="button"
                onClick={() => setMode('pin')}
                className={
                  mode === 'pin'
                    ? 'flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-herb-600/30 bg-herb-50 px-3 py-1.5 text-xs font-semibold text-herb-800'
                    : 'flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-cream'
                }
              >
                <Hash className="h-3.5 w-3.5" /> Code PIN
              </button>
            </div>

            <form
              className="mt-4 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                loginMutation.mutate();
              }}
            >
              <Field label="Adresse email" htmlFor="email" required>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="operateur@mapara.tn"
                    className="pl-9"
                  />
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-ink-ghost pointer-events-none" />
                </div>
              </Field>

              {mode === 'password' ? (
                <Field label="Mot de passe" htmlFor="password" required>
                  <div className="relative">
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="••••••••••••"
                      className="pl-9"
                    />
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-ink-ghost pointer-events-none" />
                  </div>
                </Field>
              ) : (
                <Field label="Code PIN (4–8 chiffres)" htmlFor="pin" required>
                  <div className="relative">
                    <Input
                      id="pin"
                      type="password"
                      inputMode="numeric"
                      autoComplete="off"
                      pattern="\d{4,8}"
                      required
                      value={pin}
                      onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder="••••"
                      className="pl-9 tracking-[0.4em]"
                    />
                    <Hash className="absolute left-3 top-2.5 h-4 w-4 text-ink-ghost pointer-events-none" />
                  </div>
                </Field>
              )}

              {loginMutation.isError && (
                <Alert tone="alarm" title="Accès refusé">
                  {mode === 'pin'
                    ? 'PIN invalide — vérifiez auprès de votre administrateur.'
                    : 'Identifiants invalides ou session expirée.'}
                </Alert>
              )}

              <Button
                type="submit"
                variant="primary"
                size="md"
                className="w-full mt-2"
                disabled={loginMutation.isPending}
                icon={mode === 'pin' ? <KeyRound className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
              >
                {loginMutation.isPending ? 'Authentification…' : 'Accéder à la console'}
              </Button>
            </form>

            <div className="mt-6 border-t border-border pt-4 text-center">
              <p className="text-[11px] text-ink-faint leading-relaxed">
                Sécurisé par argon2id · cookies httpOnly · console interne maparatunisie.tn
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
